import PropTypes from "prop-types";
import React from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import Button from "layout/atoms/Button";
import ContentBox from "layout/atoms/ContentBox";
import Form from "layout/atoms/Form";
import InputCheckbox from "layout/atoms/InputCheckbox";
import InputText from "layout/atoms/InputText";
import Modal from "layout/atoms/Modal";
import Select from "layout/atoms/Select";

import {
  declineIdea,
  selectCurrentBoardData,
  selectIdeasStatus,
} from "store/currentBoardSlice";
import { closeModal } from "store/modalsSlice";
import { addToast } from "store/toastsSlice";
import { Text } from "./styles";

const DeclineIdeaModal = ({ ideaId, boardId }) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "boardsDetails.declineModal",
  });

  const {
    register,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: {
      reason: "",
      comment: "",
      duplicateOfIdeaId: "",
      mergeVotes: true,
    },
  });

  const dispatch = useDispatch();
  const currentBoardData = useSelector(selectCurrentBoardData);
  const { declineIdea: declineStatus } = useSelector(selectIdeasStatus);
  const ideas = currentBoardData?.ideas?.ideas || [];
  const ideaTitle = ideas.find((idea) => idea.id === ideaId)?.title;
  const reasonValue = watch("reason");

  const declineReasons = [
    { value: "", name: t("selectReason") },
    { value: "DUPLICATE", name: t("reasons.DUPLICATE") },
    { value: "NOT_APPLICABLE", name: t("reasons.NOT_APPLICABLE") },
    { value: "NOT_RELEVANT", name: t("reasons.NOT_RELEVANT") },
    { value: "OTHER", name: t("reasons.OTHER") },
  ];

  // Generate idea options for dropdown, excluding the current idea being declined
  const getIdeaOptions = () => {
    const ideas = currentBoardData.ideas.ideas || [];
    const filteredIdeas = ideas.filter((idea) => idea.id !== ideaId);

    // Sort ideas alphabetically by title
    const sortedIdeas = [...filteredIdeas].sort((a, b) =>
      (a.title || "").localeCompare(b.title || "", undefined, {
        sensitivity: "base",
      }),
    );

    const ideaOptions = [
      { value: "", name: t("selectIdea") || "Select an idea..." },
    ];

    sortedIdeas.forEach((idea) => {
      ideaOptions.push({
        value: idea.id,
        name: `${idea.title} (${idea.id})` || `(${idea.id})`,
      });
    });

    return ideaOptions;
  };

  const handleClose = () => {
    dispatch(closeModal("declineIdeaModal"));
    reset();
  };

  return (
    <Modal open onClose={handleClose}>
      <Form
        onSubmit={(e) => {
          e.preventDefault();

          const formData = new FormData(e.target);
          const reason = formData.get("reason");
          const comment = formData.get("comment") || null;
          const duplicateOfIdeaId =
            reason === "DUPLICATE"
              ? formData.get("duplicateOfIdeaId") || null
              : null;
          const mergeVotes =
            reason === "DUPLICATE" && duplicateOfIdeaId
              ? watch("mergeVotes")
              : null;

          const requestData = {
            reason,
            comment,
            duplicateOfIdeaId,
            mergeVotes,
          };

          dispatch(declineIdea({ data: requestData, ideaId, boardId }))
            .unwrap()
            .then(() => {
              dispatch(
                addToast({
                  title: "toasts.ideaDeclined.title",
                  description: "toasts.ideaDeclined.description",
                  type: "success",
                }),
              );
              handleClose();
            });
        }}
      >
        <ContentBox
          header={<h3>{t("title")}</h3>}
          footer={
            <>
              <div />
              <div>
                <Button
                  type="button"
                  size="lg"
                  color="white"
                  onClick={handleClose}
                  disabled={declineStatus === "loading"}
                >
                  {t("cancel")}
                </Button>
                <Button
                  type="submit"
                  size="lg"
                  color="primary"
                  loading={declineStatus === "loading"}
                >
                  {t("declineIdea")}
                </Button>
              </div>
            </>
          }
        >
          <Text>
            {ideaTitle && (
              <p>
                {t("aboutToDecline")} <strong>{ideaTitle}</strong>.
              </p>
            )}
            <p>{t("description")}</p>
          </Text>
          <Select
            name="reason"
            label={t("reasonLabel")}
            register={register}
            options={declineReasons}
            validation={{
              required: true,
            }}
            errors={errors.reason}
          />

          {reasonValue === "DUPLICATE" && (
            <>
              <Select
                name="duplicateOfIdeaId"
                label={t("duplicateIdeaLabel")}
                register={register}
                options={getIdeaOptions()}
                validation={{
                  required: true,
                }}
                errors={errors.duplicateOfIdeaId}
              />

              <InputCheckbox
                checked={watch("mergeVotes")}
                onChange={(e) => {
                  setValue("mergeVotes", e.target.checked);
                }}
              >
                {t("mergeVotesLabel")}
              </InputCheckbox>
            </>
          )}
          <InputText
            type="textarea"
            name="comment"
            label={t("commentLabel")}
            placeholder={t("commentPlaceholder")}
            register={register}
            errors={errors.comment}
          />
        </ContentBox>
      </Form>
    </Modal>
  );
};

DeclineIdeaModal.propTypes = {
  ideaId: PropTypes.string.isRequired,
  boardId: PropTypes.string.isRequired,
};

export default DeclineIdeaModal;
